import { motion } from 'framer-motion';
import styled from 'styled-components';
import { FaClock, FaCalendarCheck, FaFileInvoiceDollar } from 'react-icons/fa';

const HighlightsSection = styled.section`
  padding: 12rem 1.5rem;
  background: ${props => props.theme.colors.background};
  position: relative;
  overflow: hidden;
`;

const HighlightsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 4rem;
  max-width: 1400px;
  margin: 0 auto;

  @media (min-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 6rem;
  }
`;

const HighlightCard = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  padding: 0;
  background: none;
  border: none;
  box-shadow: none;
`;

const IconWrapper = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${props => props.bgColor};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  color: white;
`;

const CardTitle = styled.h3`
  font-size: 1.875rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text.primary};
  margin: 1.5rem 0 1rem;
`;

const CardDescription = styled.p`
  font-size: 1.125rem;
  color: ${props => props.theme.colors.text.secondary};
  line-height: 1.7;
  margin: 0;
`;

const Title = styled(motion.h2)`
  font-size: clamp(2.5rem, 5vw, 3.5rem);
  font-weight: 600;
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: 1.5rem;
  letter-spacing: -0.02em;
  
  span {
    color: ${props => props.theme.colors.primary};
  }
`;

const Description = styled(motion.p)`
  font-size: 1.25rem;
  color: ${props => props.theme.colors.text.secondary};
  line-height: 1.6;
  max-width: 600px;
  margin: 0 auto;
`;

const Metric = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: ${props => props.color};
  
  span {
    display: block;
  }
  
  small {
    font-size: 1rem;
    color: ${props => props.theme.colors.text.secondary};
    font-weight: 500;
  }
`;

const Highlights = () => {
  const highlights = [
    {
      icon: <FaClock />,
      title: "Automated Timesheets",
      description: "Effortlessly track time with smart automation. Our AI automatically detects and logs your work activities, saving hours on manual entry.",
      metric: "15hrs",
      metricLabel: "Saved per Month",
      color: props => props.theme.colors.primary
    },
    {
      icon: <FaCalendarCheck />,
      title: "Event Management",
      description: "Streamline your events with integrated scheduling, resource allocation, and team coordination tools.",
      metric: "300%",
      metricLabel: "Efficiency Boost",
      color: props => props.theme.colors.secondary
    },
    {
      icon: <FaCalendarCheck />,
      title: "Booking Links",
      description: "Create professional booking pages that sync with your calendar. Let clients schedule meetings without the back-and-forth.",
      metric: "24/7",
      metricLabel: "Booking Availability",
      color: "#06b6d4"
    },
    {
      icon: <FaFileInvoiceDollar />,
      title: "Estimate Generation",
      description: "Generate professional estimates in seconds. Smart templates and historical data help you price accurately every time.",
      metric: "2x",
      metricLabel: "Faster Quotes",
      color: "#0ea5e9"
    }
  ];

  return (
    <HighlightsSection>
      <HighlightsGrid>
        {highlights.map((highlight, index) => (
          <HighlightCard
            key={index}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            <IconWrapper bgColor={highlight.color}>
              {highlight.icon}
            </IconWrapper>
            <div>
              <CardTitle>{highlight.title}</CardTitle>
              <CardDescription>{highlight.description}</CardDescription>
            </div>
            <Metric color={highlight.color}>
              <span>{highlight.metric}</span>
              <small>{highlight.metricLabel}</small>
            </Metric>
          </HighlightCard>
        ))}
      </HighlightsGrid>
    </HighlightsSection>
  );
};

export default Highlights;
